import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { countries } from "@/constants/countryCodes";
import { registrationTypes } from "@/constants/registrationTypes";
import { states } from "@/constants/states";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Tabs } from "@radix-ui/themes";
import { Building2, CheckCircle, CircleAlert, Loader2, Mail, Phone, SearchX, Settings, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FieldErrors, useFieldArray, useForm } from "react-hook-form";
import { UomCombobox } from "@/components/uom-combobox";
import { navigate } from "wouter/use-browser-location";
import logo from "/asset/logo-removebg-preview.png";
import TinSearch from "@/components/modal-tin";
import { format } from "date-fns";
import { DocumentType } from "@/types/form";
import { z } from "zod";
import { classificationCodes } from "@/constants/classificationCodes";
import { taxTypes } from "@/constants/taxTypes";
import { invoiceItemSchema } from "@/schema/schema";
import { selfServiceOcrInvoiceSchema } from "@/schema/self-service.schema";
import { useSelfService } from "@/hooks/useSelfService";
import {
  DEFAULT_DECIMAL_PRECISION,
  RATE_INPUT_STEP,
  TOTAL_PRECISION,
  format as formatAtPrecision,
  formatWithSeparators,
  roundTo,
  stepForPrecision,
  toLineAmount,
} from "@/utils/decimal";
import { deriveDocumentTotals } from "@/utils/documentTotals";

type ReceiptItemType = {
  itemDescription: string;
  itemPrice: number;
  itemQuantity: number;
  itemSubtotal: number;
  itemTaxType: string;
  itemTaxRate: number;
  itemTaxAmount: number;
  itemFeeRate: number;
  itemFeeAmount: number;
  itemDiscountRate: number;
  itemDiscountAmount: number;
}

// type receiptLineItem = {
//   itemDescription: string,
//   itemPrice: number,
//   itemQuantity: number,
//   itemSubtotal: number,
//   itemTaxRate: number,
//   itemTaxAmount: number,
//   itemServiceChargeRate: number,
//   itemServiceChargeAmount: number,
//   itemDiscountRate: number,
//   itemDiscountAmount: number
// }

type ReceiptDataType = {
  companyName: string;
  invoiceNumber: string;
  invoiceDateTime: string; // or use Date type if you'll convert it
  invoiceItems: ReceiptItemType[];
  additionalDiscountAmount: number;
  additionalFeeAmount: number;
  totalSalesTaxAmount: number;
  totalPayableAmount: number;
  totalRoundingAmount: number;
  originalInvoiceNumber: string;
}

type CompanyProfileType = {
  id: string;
  name: string;
  tin: string;
  registrationType: string; // or more specific: "BRN" | "NRIC" | "PASSPORT" etc.
  registrationNumber: string;
  sstRegistrationNumber: string | null;
  tourismTaxRegistrationNumber: string | null;
  email: string;
  msicCode: string;
  businessActivityDescription: string;
  contactNumber: string;
  addressId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  deletedAt: string | Date | null;
  baseCurrency: string; // or more specific: "MYR" | "USD" | "SGD" etc.
  addressLine0: string;
  addressLine1: string | null;
  addressLine2: string | null;
  postalZone: string;
  cityName: string;
  state: string; // or more specific if you know possible values: "01" | "02" | ... | "14"
  country: string; // or more specific: "MYS" | "SGP" | "THA" etc.
  deletionReason: string | null;
  ownerId: string;
  businessProfileId: string;
}

type TaxType = "01" | "02" | "03" | "04" | "05" | "06" | "E";

const SelfServiceOcrDocumentPage = () => {
  const formatDecimal = (v: number | null | undefined) =>
    formatAtPrecision(Number(v ?? 0), DEFAULT_DECIMAL_PRECISION);
  const PUBLIC_STEP = stepForPrecision(DEFAULT_DECIMAL_PRECISION);
  const round = (v: number | null | undefined) => toLineAmount(Number(v ?? 0));
  const roundTotal = (v: number | null | undefined) =>
    roundTo(Number(v ?? 0), TOTAL_PRECISION);
  const formatTotalAmount = (v: number | null | undefined) =>
    formatWithSeparators(Number(v ?? 0), TOTAL_PRECISION);
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openTinSearch, setOpenTinSearch] = useState<boolean>(false);
  const receiptData: ReceiptDataType = window.history.state?.receiptData ?? null;
  const companyProfile: CompanyProfileType = window.history.state?.companyProfile ?? null;
  const [totals, setTotals] = useState({
    totalExcludingTax: 0,
    totalIncludingTax: 0,
    totalTaxAmount: 0,
    totalPayableAmount: 0,
  });

  const {
    createInvoicSelfServiceOcr
  } = useSelfService()

  const defaultValue = useMemo(() => {
    const commonFieldsValue = {
      // Invoice Details
      documentType: DocumentType.INVOICE, // Should match `DocumentType` enum
      invoiceDate: format(new Date(receiptData.invoiceDateTime), "yyyy-MM-dd"), // Zod expects string
      invoiceTime: format(new Date(receiptData.invoiceDateTime), "HH:mm:ss"), // Zod expects string
      issuerDigitalSignature: "NA",
      invoiceCurrencyCode: "MYR",
      currencyExchangeRate: 1.0, // Nullable number
      originalInvoiceNumber: receiptData.originalInvoiceNumber,

      // Frequency of Billing Information
      frequencyOfBilling: null,
      billingPeriodStartDate: null,
      billingPeriodEndDate: null,

      // Invoice Line Items
      items: [], // Array of `invoiceItemSchema`

      // Payment & Prepayment Information
      paymentMode: null, // Nullable enum
      supplierBankAccountNumber: null, // Nullable number
      paymentTerms: null,
      prepaymentAmount: null, // Nullable number
      prepaymentDate: null,
      prepaymentReferenceNumber: null,
      billReferenceNumber: null,

      //Shipping Information
      shipping: undefined,

      // Import & Export Information
      customs: undefined,
    };

    const companyAsSupplierDefaultValue = {
      ...commonFieldsValue,
      // Buyer Information
      buyerName: "",
      buyerTin: "",
      buyerRegistrationType: "",
      buyerRegistrationNumber: "",
      buyerSstRegistrationNumber: "",
      buyerEmail: "",
      buyerContact: "",
      buyerAddressLine0: "",
      buyerAddressLine1: null,
      buyerAddressLine2: null,
      buyerCity: "",
      buyerPostcode: null,
      buyerCountry: "",
      buyerState: "",

      // Supplier Information
      supplierName: companyProfile.name,
      supplierTin: companyProfile.tin,
      supplierRegistrationType: companyProfile.registrationType,
      supplierRegistrationNumber: companyProfile.registrationNumber,
      supplierSstRegistrationNumber: companyProfile.sstRegistrationNumber ?? null, // Optional
      supplierMsicCode: companyProfile.msicCode,
      supplierBusinessActivityDescription: companyProfile.businessActivityDescription,
      supplierTourismTaxRegistrationNumber: companyProfile.tourismTaxRegistrationNumber,
      supplierEmail: companyProfile.email ?? null,
      supplierContact: companyProfile.contactNumber,
      supplierAddressLine0: companyProfile.addressLine0,
      supplierAddressLine1: companyProfile.addressLine1 ?? null,
      supplierAddressLine2: companyProfile.addressLine2 ?? null,
      supplierCity: companyProfile.cityName,
      supplierPostcode: companyProfile.postalZone ?? null,
      supplierCountry: companyProfile.country,
      supplierState: companyProfile.state,

      roundingAmount: receiptData.totalRoundingAmount,
      invoiceAdditionalDiscountAmount: receiptData.additionalDiscountAmount,
      invoiceAdditionalFeeAmount: receiptData.additionalFeeAmount,
    };

    return companyAsSupplierDefaultValue
  }, []);

  type FormValues = z.infer<typeof selfServiceOcrInvoiceSchema>

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(selfServiceOcrInvoiceSchema),
    defaultValues: defaultValue,
  });

  const [items = []] = watch(["items"]);
  const invoiceAdditionalDiscountAmount = watch("invoiceAdditionalDiscountAmount") ?? 0;
  const invoiceAdditionalFeeAmount = watch("invoiceAdditionalFeeAmount") ?? 0;
  const roundingAmount = watch("roundingAmount") ?? 0;
  

  const calculateTotalTaxPerType = () => {
    const items = getValues("items") || [];

    const map = new Map<"01" | "02" | "03" | "04" | "05" | "06" | "E", number>();

    for (const item of items) {
      if (!item || item._delete) continue;

      const taxType = item.taxType;
      const taxAmount = Number(item.taxAmount ?? 0);

      if (!taxType) continue;

      map.set(taxType, (map.get(taxType) || 0) + taxAmount);
    }

    const grouped = Array.from(map.entries()).map(([taxType, taxAmount]) => ({
      taxType,
      taxAmount: Number(formatDecimal(taxAmount)),
    }));

    return grouped;
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  useEffect(() => {
    reset(defaultValue);

    receiptData.invoiceItems.forEach((item: ReceiptItemType) => {

      append({
        id: undefined, // Ensure a unique ID
        classification: "022",
        description: item.itemDescription || "",
        unitPrice: item.itemPrice || 0,
        taxType: item.itemTaxType as TaxType,
        taxRate: item.itemTaxRate || 0,
        taxAmount: item.itemTaxAmount || 0,
        subtotal: item.itemSubtotal || 0,
        totalExcludingTax: item.itemSubtotal || 0,
        quantity: item.itemQuantity || 1,
        discountRate: item.itemDiscountRate || 0,
        discountAmount: item.itemDiscountAmount || 0,
        feeRate: item.itemFeeRate || 0,
        feeAmount: item.itemFeeAmount || 0,
        codeNumber: "NA",
        detailsOfTaxExemption: null,
        amountExemptedFromTax: null,
        productTariffCode: null,
        countryOfOrigin: "MYS",
        measurement: null,
        total: Number(formatDecimal((item.itemSubtotal - (item?.itemDiscountAmount ?? 0) + item.itemTaxAmount + (item?.itemFeeAmount ?? 0)))) || Number(0)
      });
      // handleCalculation(index, 'quantity', item.quantity)
    })
  }, [])
  

  const onSubmit = async(data: FormValues) => {
    setIsLoading(true)

    setTimeout(() => {}, 3000)
    const lineItems = items.map((item: z.infer<typeof invoiceItemSchema>) => ({
      itemCode: item.codeNumber,
      classification: item.classification,
      description: item.description,
      // due to form receive all input as string, convert the
      // string to number again to match api schema
      unitPrice: Number(item.unitPrice),
      taxType: item.taxType,
      taxRate: Number(item.taxRate),
      taxAmount: Number(item.taxAmount),
      detailsOfTaxExemption: item.detailsOfTaxExemption,
      amountExemptedFromTax: item.amountExemptedFromTax,
      subtotal: item.subtotal,
      totalExcludingTax: item.totalExcludingTax,
      quantity: Number(item.quantity),
      measurement: item.measurement,
      discountRate: Number(item.discountRate),
      discountAmount: item.discountAmount,
      feeRate: Number(item.feeRate),
      feeAmount: item.feeAmount,
      productTariffCode: item.productTariffCode,
      countryOfOrigin: item.countryOfOrigin,
    }));

    const invoiceDataToBeSave = {
      // supplier information
      supplierCode: null,
      supplierRegistrationType: data.supplierRegistrationType,
      supplierRegistrationNumber: data.supplierRegistrationNumber,
      supplierTin: data.supplierTin,
      supplierName: data.supplierName,
      supplierSstRegistrationNumber: data.supplierSstRegistrationNumber,
      supplierMsicCode: data.supplierMsicCode,
      supplierTourismTaxRegistrationNumber:
        data.supplierTourismTaxRegistrationNumber,
      supplierBusinessActivityDescription:
        data.supplierBusinessActivityDescription,
      supplierEmail: data.supplierEmail,
      supplierContact: data.supplierContact,
      supplierAddressLine0: data.supplierAddressLine0,
      supplierAddressLine1: data.supplierAddressLine1,
      supplierAddressLine2: data.supplierAddressLine2,
      supplierCity: data.supplierCity,
      supplierPostcode: data.supplierPostcode,
      supplierState: data.supplierState,
      supplierCountry: data.supplierCountry,

      // buyer information
      buyerCode: null,
      buyerRegistrationType: data.buyerRegistrationType,
      buyerRegistrationNumber: data.buyerRegistrationNumber,
      buyerTin: data.buyerTin,
      buyerName: data.buyerName,
      buyerSstRegistrationNumber: data.buyerSstRegistrationNumber,
      buyerEmail: data.buyerEmail,
      buyerContact: data.buyerContact,
      buyerAddressLine0: data.buyerAddressLine0,
      buyerAddressLine1: data.buyerAddressLine1,
      buyerAddressLine2: data.buyerAddressLine2,
      buyerCity: data.buyerCity,
      buyerPostcode: data.buyerPostcode,
      buyerState: data.buyerState,
      buyerCountry: data.buyerCountry,

      // invoice details
      version: "1.0", // Max 5 characters
      typeCode: "01", // Enum, limited to 2 characters
      originalInvoiceNumber: data.originalInvoiceNumber,
      originalInvoiceReference: null,
      codeNumber: null, // Max 50 characters
      invoiceDate: new Date(), // Transformed from string datetime
      invoiceTime: new Date(), // Transformed from string datetime
      issuerDigitalSignature: "123456789abceds",
      currencyCode: "MYR", // Max 3 characters
      currencyExchangeRate: Number(data.currencyExchangeRate), // Default 1 if not provided
      docDate: null, // Transformed from string datetime
      jobCode: null,

      // Frequent of Billing Information
      frequencyOfBilling: data.frequencyOfBilling,
      billingPeriodStartDate: data.billingPeriodStartDate
        ? new Date(data.billingPeriodStartDate)
        : null,
      billingPeriodEndDate: data.billingPeriodEndDate
        ? new Date(data.billingPeriodEndDate)
        : null,

      // invoice line item
      lineItems: lineItems, // Must have at least 1 line item

      // Payment and Prepayment Information
      paymentMode: data.paymentMode,
      supplierBankAccountNumber: data.supplierBankAccountNumber, // Max 150 characters
      paymentTerms: data.paymentTerms, // Max 300 characters
      prepaymentAmount: data.prepaymentAmount, // Default 0
      prepaymentDate: data.prepaymentDate
        ? new Date(data.prepaymentDate)
        : null,
      prepaymentTime: null,
      prepaymentReferenceNumber: data.prepaymentReferenceNumber, // Max 150 characters
      billReferenceNumber: data.billReferenceNumber, // Max 150 characters

      //shipping info
      shipping: null,

      //imports and exports
      customs: null,

      // LegalMonetaryTotal
      totalExcludingTax: roundTotal(totals.totalExcludingTax),
      totalIncludingTax: roundTotal(totals.totalIncludingTax),
      totalPayableAmount: roundTotal(totals.totalPayableAmount),
      totalNetAmount: null, // Default 0
      totalDiscountValue: null, // Default 0
      totalFeeChargeAmount: null, // Default 0

      // taxTotal
      totalTaxAmount: roundTotal(totals.totalTaxAmount),
      roundingAmount: data.roundingAmount, // Default 0
      totalTaxableAmountPerTaxType: null, // Default 0
      totalTaxAmountPerTaxType: calculateTotalTaxPerType(),
      taxType: "06" as const, // Default to TaxCode.NotApplicable

      // AllowanceCharge
      invoiceAdditionalDiscountAmount: data.invoiceAdditionalDiscountAmount, // Default 0
      invoiceAdditionalFeeAmount: data.invoiceAdditionalFeeAmount, // Default 0

      businessId: companyProfile.id,
      branchId: companyProfile.id,
      ownerId: companyProfile.ownerId
    };

    await createInvoicSelfServiceOcr(invoiceDataToBeSave, {
      onSuccess: (response) => {
        setSubmitted(true)
        toast({
          title: "Invoice Saved",
          description: `The invoice ${response.data.codeNumber} successfully saved.`,
          duration: 5000,
          icon: <CheckCircle size={30} className="animate-spin-once text-green-400"/>,
        });
      },
      onError: (error: any) => {
        setIsLoading(false)
        toast({
          title: "Failed",
          variant: "destructive",
          description: `Failed to save invoice. Please try again. ${JSON.stringify(error.response.data.details)}`,
          icon: <CircleAlert size={30} className="animate-spin-once"/>,
          duration: 4000,
        });
      },
    });
  };

  const onError = (errors: FieldErrors<FormValues>) => {
    console.error(errors)
    const description = Object.values(errors)[0]?.message as string;
    toast({
      title: "Action failed",
      variant: "destructive",
      description: description,
      icon: <CircleAlert size={30} className="animate-spin-once"/>,
      duration: 4000,
    });
  };

  const handleCalculation = useCallback((
    index: number,
    field: string,
    value: string | number
  ) => {
    const items = getValues("items");
    const item = items[index];

    if (!item) return;
    const updatedItem = {
      ...item,
      [field]: Number(value) || 0,
    };
    const toSafeNumber = (value: any): number => {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    const quantity = toSafeNumber(updatedItem.quantity);
    const unitPrice = toSafeNumber(updatedItem.unitPrice);
    const discountRate = toSafeNumber(updatedItem.discountRate);
    const feeRate = toSafeNumber(updatedItem.feeRate);

    const subtotal = quantity * unitPrice;
    const discountAmount = subtotal * (discountRate / 100);
    
    // According to Mr Ng, we should follow customs
    // service charge are not included in taxable amount
    const taxableAmount = subtotal - discountAmount;

    // use taxable amount due to is the same for charge
    const feeChargeAmount = taxableAmount * (feeRate / 100);

    let taxAmount, taxRate;

    if (field === "taxAmount") {
      // If tax amount is manually entered and editable
      taxAmount = Number(value);
      taxRate = taxableAmount > 0 ? (taxAmount / taxableAmount) * 100 : 0;
    } else {
      // Normal calculation using tax rate
      taxRate = Number(updatedItem.taxRate ?? 0);
      taxAmount = taxableAmount * (taxRate / 100);
    }

    const totalExcludingTax = taxableAmount + feeChargeAmount;
    const total = totalExcludingTax + taxAmount; // also known as total after tax

    setValue(`items.${index}.subtotal`, round(subtotal), {
      shouldValidate: true,
    });
    setValue(`items.${index}.discountAmount`, round(discountAmount), {
      shouldValidate: true,
    });
    setValue(`items.${index}.feeAmount`, round(feeChargeAmount), {
      shouldValidate: true,
    });
    setValue(`items.${index}.taxRate`, Number(formatDecimal(taxRate)), {
      shouldValidate: true,
    });
    setValue(`items.${index}.taxAmount`, round(taxAmount), {
      shouldValidate: true,
    });
    setValue(`items.${index}.totalExcludingTax`, round(totalExcludingTax), {
      shouldValidate: true,
    });
    setValue(`items.${index}.total`, round(total));
  }, [getValues, setValue]);

  // totals calculation
  useEffect(() => {
    const totals = items.reduce(
      (
        acc: {
          totalExcludingTax: number;
          totalIncludingTax: number;
          totalTaxAmount: number;
          totalPayableAmount: number;
        },
        item: z.infer<typeof invoiceItemSchema>
      ) => {
        return {
          totalExcludingTax: acc.totalExcludingTax + item.totalExcludingTax,
          totalIncludingTax: acc.totalIncludingTax + item.total,
          totalTaxAmount: acc.totalTaxAmount + item.taxAmount,
          totalPayableAmount: acc.totalPayableAmount + item.total,
        };
      },
      {
        totalExcludingTax: 0,
        totalIncludingTax: 0,
        totalTaxAmount: 0,
        totalPayableAmount: 0,
      }
    );

    // based on lhdn guidelines, the rounding should be added
    setTotals(
      deriveDocumentTotals({
        rawExcludingTax: totals.totalExcludingTax,
        totalTaxAmount: calculateTotalTaxPerType().reduce(
          (sum, entry) => sum + entry.taxAmount,
          0,
        ),
        additionalDiscountAmount: invoiceAdditionalDiscountAmount,
        additionalFeeAmount: invoiceAdditionalFeeAmount,
        roundingAmount,
      }),
    );
  }, [items, handleCalculation, invoiceAdditionalDiscountAmount, invoiceAdditionalFeeAmount, roundingAmount]);

  if (!receiptData || !companyProfile) {
    return <ErrorBoundary />
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-white pt-[80px] pb-8 px-4">
      <SelfServiceNav />
      <Card className="w-full max-w-6xl mx-auto shadow pt-6">
        <CardContent>
          <form
            className="space-y-6"
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-6">
              <div className="flex flex-row">
                <h1 className="text-xl font-bold text-center md:text-left me-2">
                  Invoice
                </h1>
              </div>
              {/* Company Details - Middle */}
              <div className="flex flex-col items-center justify-center w-full text-center space-y-3 md:-ml-16">
                <div className="flex flex-row items-center justify-center gap-4">
                  <h1 className="text-2xl font-bold text-center md:text-left">
                    {getValues("supplierName")}
                  </h1>
                </div>

                <div className="flex items-center space-x-2 text-gray-600">
                  <Phone size={16} className="flex-shrink-0" />
                  <span className="text-sm">
                    {getValues("supplierContact")}
                  </span>
                </div>

                <div className="flex items-center space-x-2 text-gray-600">
                  <Mail size={16} className="flex-shrink-0" />
                  <span className="text-sm">
                    {getValues("supplierEmail")}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600">
                  <Building2 size={16} className="flex-shrink-0" />
                  <span className="text-sm">
                    {getValues("supplierAddressLine0")},{" "}
                    {getValues("supplierCity")}
                  </span>
                </div>
              </div>

              <Separator orientation="vertical" />
            </div>

            <Tabs.Root defaultValue="buyer">
              <Tabs.List color="gray" justify="start">
                <Tabs.Trigger value="buyer">
                  Buyer Information
                  <span className="text-red-600">*</span>
                </Tabs.Trigger>
              </Tabs.List>

              <Box pt="3">
                <Tabs.Content value="buyer">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">
                      Buyer Information{" "}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="buyerRegistrationType" required>
                          ID Type
                        </Label>
                        <Controller
                          name="buyerRegistrationType"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={submitted || isLoading}
                            >
                              <SelectTrigger
                                className="h-10"
                                errors={!!errors.buyerRegistrationType}
                              >
                                <SelectValue placeholder="-- Select type --" />
                              </SelectTrigger>
                              <SelectContent>
                                {registrationTypes.map((type) => (
                                  <SelectItem
                                    key={type.value}
                                    value={type.value}
                                  >
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.buyerRegistrationType && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerRegistrationType.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerRegistrationNumber" required>
                          Registration / Identification / Passport Number
                        </Label>
                        <Input
                          {...register("buyerRegistrationNumber")}
                          errors={!!errors.buyerRegistrationNumber}
                          disabled={submitted || isLoading}
                        />
                        {errors.buyerRegistrationNumber && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerRegistrationNumber.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerTin" required>
                          TIN
                        </Label>
                        <div className="flex gap-1">
                          <Input
                            {...register("buyerTin")}
                            errors={!!errors.buyerTin}
                            disabled={submitted || isLoading}
                            />
                          <Button
                            variant="outline"
                            type="button"
                            onClick={() => setOpenTinSearch(true)}
                            className="group"
                            disabled={submitted || isLoading}
                          >
                            <Settings />
                          </Button>
                        </div>
                        {errors.buyerTin && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerTin.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="buyerName" required>
                          Name
                        </Label>
                        <Input
                          {...register("buyerName")}
                          errors={!!errors.buyerName}
                          disabled={submitted || isLoading}
                        />
                        {errors.buyerName && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerName.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerSstRegistrationNumber">
                          SST Registration Number
                        </Label>
                        <Input
                          {...register("buyerSstRegistrationNumber")}
                          errors={!!errors.buyerSstRegistrationNumber}
                          disabled={submitted || isLoading}
                        />
                        {errors.buyerSstRegistrationNumber && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerSstRegistrationNumber.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerEmail" required>
                          Email
                        </Label>
                        <Input
                          {...register("buyerEmail")}
                          errors={!!errors.buyerEmail}
                          disabled={submitted || isLoading}
                        />
                        {errors.buyerEmail && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerEmail.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerContact" required>
                          Contact Number
                        </Label>
                        <Input
                          {...register("buyerContact")}
                          errors={!!errors.buyerContact}
                          disabled={submitted || isLoading}
                        />
                        {errors.buyerContact && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerContact.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="buyerAddressLine0" required>
                        Address Line 1
                      </Label>
                      <Input
                        {...register("buyerAddressLine0")}
                        errors={!!errors.buyerAddressLine0}
                        disabled={submitted || isLoading}
                      />
                      {errors.buyerAddressLine0 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerAddressLine0.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="buyerAddressLine1">
                        Address Line 2
                      </Label>
                      <Input
                        {...register("buyerAddressLine1")}
                        errors={!!errors.buyerAddressLine1}
                        disabled={submitted || isLoading}
                      />
                    </div>
                    <div>
                      <Label htmlFor="buyerAddressLine2">
                        Address Line 3
                      </Label>
                      <Input
                        {...register("buyerAddressLine2")}
                        errors={!!errors.buyerAddressLine2}
                        disabled={submitted || isLoading}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="buyerCity" required>
                          City
                        </Label>
                        <Input
                          {...register("buyerCity")}
                          errors={!!errors.buyerCity}
                          disabled={submitted || isLoading}
                        />
                        {errors.buyerCity && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerCity.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerPostcode">Postcode</Label>
                        <Input
                          {...register("buyerPostcode")}
                          errors={!!errors.buyerPostcode}
                          disabled={submitted || isLoading}
                        />
                        {errors.buyerPostcode && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerPostcode.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerCountry" required>
                          Country
                        </Label>
                        <Controller
                          name="buyerCountry"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={submitted || isLoading}
                            >
                              <SelectTrigger
                                className="h-10"
                                errors={!!errors.buyerCountry}
                              >
                                <SelectValue placeholder="-- Select Country --" />
                              </SelectTrigger>
                              <SelectContent>
                                {countries.map((country) => (
                                  <SelectItem
                                    key={country.Code}
                                    value={country.Code}
                                  >
                                    {country.Country}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.buyerCountry && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerCountry.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerState" required>
                          State
                        </Label>
                        <Controller
                          name="buyerState"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={submitted || isLoading}
                            >
                              <SelectTrigger
                                className="h-10"
                                errors={!!errors.buyerState}
                              >
                                <SelectValue placeholder="-- Select State --" />
                              </SelectTrigger>
                              <SelectContent>
                                {states.map((state) => (
                                  <SelectItem
                                    key={state.Code}
                                    value={state.Code}
                                  >
                                    {state.State}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.buyerState && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerState.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Tabs.Content>
              </Box>
            </Tabs.Root>

            {/* Items Section */}
            <div className="space-y-4">
              <Table>
                <TableHeader>
                <TableRow>
                  <TableHead className="min-w-36">Classification</TableHead>
                  <TableHead className="min-w-36">Description</TableHead>
                  <TableHead className="min-w-36">Unit Price</TableHead>
                  <TableHead className="min-w-36">Quantity</TableHead>
                  <TableHead className="min-w-36">Tax Type</TableHead>
                  <TableHead className="min-w-36">Tax Rate</TableHead>
                  <TableHead className="min-w-36">Tax Amount</TableHead>
                  <TableHead className="min-w-36">Details of Tax Exemption</TableHead>
                  <TableHead className="min-w-36">Amount Exempted from Tax</TableHead>
                  <TableHead className="min-w-36">Subtotal</TableHead>
                  <TableHead className="min-w-36">Total Excluding Tax</TableHead>
                  <TableHead className="min-w-36">Measurement</TableHead>
                  <TableHead className="min-w-36">Discount Rate</TableHead>
                  <TableHead className="min-w-36">Discount Amount</TableHead>
                  <TableHead className="min-w-36">Fee / Charge Rate</TableHead>
                  <TableHead className="min-w-36">Fee / Charge Amount</TableHead>
                  <TableHead className="min-w-36">Product Tariff Code</TableHead>
                  <TableHead className="min-w-36">Country of Origin</TableHead>
                  <TableHead className="min-w-36">Total</TableHead>
                </TableRow>
              </TableHeader>
                <TableBody>
                  {fields.map((field, index) => {
                    return (
                      <TableRow key={field.id}>
                        <TableCell>
                          <Controller
                            name={`items.${index}.classification`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                                disabled={submitted || isLoading}
                              >
                                <SelectTrigger className="w-64 text-left">
                                  <SelectValue placeholder="-- Select Classification --" />
                                </SelectTrigger>
                                <SelectContent className="w-96">
                                  {classificationCodes.map((code) => (
                                    <SelectItem key={code.Code} value={code.Code}>
                                      {code.Code} - {code.Description}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="w-72"
                            {...register(`items.${index}.description`)}
                            disabled={submitted || isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={0.01}
                            {...register(`items.${index}.unitPrice`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) =>
                              handleCalculation(
                                index,
                                "unitPrice",
                                e.target.value
                              )
                            }
                            disabled={submitted || isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="any"
                            {...register(`items.${index}.quantity`, {
                              valueAsNumber: true,
                            })}
                            className="w-20"
                            onChange={(e) =>
                              handleCalculation(index, "quantity", e.target.value)
                            }
                            disabled={submitted || isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`items.${index}.taxType`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={submitted || isLoading}
                              >
                                <SelectTrigger className="text-left w-56">
                                  <SelectValue placeholder="-- Select tax type --" />
                                </SelectTrigger>
                                <SelectContent className="">
                                  {taxTypes.map((taxType) => (
                                    <SelectItem
                                      key={taxType.Code}
                                      value={taxType.Code}
                                    >
                                      {taxType.Code} - {taxType.Description}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            {...register(`items.${index}.taxRate`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) =>
                              handleCalculation(index, "taxRate", e.target.value)
                            }
                            disabled={submitted || isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            {...register(`items.${index}.taxAmount`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) =>
                              handleCalculation(
                                index,
                                "taxAmount",
                                e.target.value
                              )
                            }
                            disabled={submitted || isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            {...register(`items.${index}.detailsOfTaxExemption`)}
                            disabled={submitted || isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            {...register(`items.${index}.amountExemptedFromTax`, {valueAsNumber: true})}
                            disabled={submitted || isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            {...register(`items.${index}.subtotal`)}
                            className="w-20"
                            disabled
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            {...register(`items.${index}.totalExcludingTax`)}
                            className="w-24"
                            disabled
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            control={control}
                            name={`items.${index}.measurement`}
                            render={({ field }) => (
                              <UomCombobox
                                value={field.value}
                                onChange={field.onChange}
                                disabled={submitted || isLoading}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={RATE_INPUT_STEP}
                            {...register(`items.${index}.discountRate`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) =>
                              handleCalculation(
                                index,
                                "discountRate",
                                e.target.value
                              )
                            }
                            className="w-20"
                            disabled={submitted || isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={PUBLIC_STEP}
                            {...register(`items.${index}.discountAmount`, { valueAsNumber: true })}
                            onChange={(e) => handleCalculation(index, "discountAmount", e.target.value)}
                            className="w-20"
                            disabled
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={RATE_INPUT_STEP}
                            {...register(`items.${index}.feeRate`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) =>
                              handleCalculation(index, "feeRate", e.target.value)
                            }
                            className="w-20"
                            disabled={submitted || isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={RATE_INPUT_STEP}
                            disabled
                            {...register(`items.${index}.feeAmount`, { valueAsNumber: true })}
                            onChange={(e) => handleCalculation(index, "feeAmount", e.target.value)}
                            className="w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            {...register(`items.${index}.productTariffCode`)}
                            className="w-20"
                            disabled={submitted || isLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`items.${index}.countryOfOrigin`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                                disabled={submitted || isLoading}
                              >
                                <SelectTrigger className="w-52 text-left">
                                  <SelectValue placeholder="-- Select Country --" />
                                </SelectTrigger>
                                <SelectContent>
                                  {countries.map((country) => (
                                    <SelectItem
                                      key={country.Code}
                                      value={country.Code}
                                    >
                                      {country.Country}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </TableCell>
                        <TableCell className="">
                          <Input
                            type="number"
                            {...register(`items.${index}.total`)}
                            disabled
                            className="border-0"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                            className="p-0 h-auto"
                            disabled={submitted || isLoading}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Totals */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total Excluding Tax:</span>
                  <span>
                    RM {formatTotalAmount(Number(totals?.totalExcludingTax || 0))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Including Tax:</span>
                  <span>
                    RM {formatTotalAmount(Number(totals?.totalIncludingTax || 0))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Tax Amount:</span>
                  <span>RM {formatTotalAmount(Number(totals?.totalTaxAmount || 0))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="content-center">Invoice Additional Discount Amount:</span>
                  <span>
                    <Input
                      disabled={submitted || isLoading}
                      className="text-right h-8"
                      {...register("invoiceAdditionalDiscountAmount", {
                        setValueAs: (value) => Number(value)
                      })}
                    />
                    {errors.invoiceAdditionalDiscountAmount && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.invoiceAdditionalDiscountAmount.message?.toString()}
                      </p>
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="content-center">Invoice Additional Fee Amount:</span>
                  <span>
                    <Input
                      disabled={submitted || isLoading}
                      className="text-right h-8"
                      {...register("invoiceAdditionalFeeAmount", {
                        setValueAs: (value) => Number(value)
                      })}
                    />
                    {errors.invoiceAdditionalFeeAmount && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.invoiceAdditionalFeeAmount.message?.toString()}
                      </p>
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="content-center">Rounding Amount:</span>
                  <span>
                    <Input
                      disabled={submitted || isLoading}
                      className="text-right h-8"
                      {...register("roundingAmount", {
                        setValueAs: (value) => Number(value)
                      })}
                    />
                    {errors.roundingAmount && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.roundingAmount.message?.toString()}
                      </p>
                    )}
                  </span>
                </div>
                {/* <div className="flex justify-between">
                  <span>Invoice Additional Discount Description:</span>
                  <span className="w-80">
                    <textarea
                      className={cn(
                        "text-right flex h-32 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-base ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-slate-950 placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:file:text-slate-50 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300",
                        {
                          "border-red-500 dark:border-red-500 focus-visible:ring-red-500 dark:focus-visible:ring-red-500":
                            !!errors?.invoiceAddtionalDiscountDescription,
                        }
                      )}
                    />
                  </span>
                </div> */}
                <Separator />
                <div className="flex justify-between font-bold">
                  <span>Total Payable Amount:</span>
                  <span>
                    RM {formatTotalAmount(Number(totals?.totalPayableAmount || 0))}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              {submitted ? (
                <Button
                  type="button"
                  onClick={() => navigate("/self-service-ocr")}
                >
                  Close
                </Button>
              ) : (
                <Button
                  type="submit"
                  className="w-full md:w-auto"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin text-primary" />
                      Submitting
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
        <TinSearch
          isOpen={openTinSearch}
          onClose={() => setOpenTinSearch(false)}
          onSelect={(value) => setValue("buyerTin", value)}
        />
      </Card>
    </div>
  );
};

const SelfServiceNav = () => {
  return (
    <nav className="w-full fixed z-30 top-0 p-2 bg-white shadow-md border-b border-slate-200 flex justify-center">
      <img className="h-16" 
        // src="/logo-removebg-preview.png"
        src={logo}
      />
    </nav>
  );
};

// const LoadingIndicator = () => {
//   return (
//     <div className="fixed inset-0 bg-background flex items-center justify-center">
//       <div className="flex flex-col items-center space-y-4">
//         <Loader2 className="h-8 w-8 animate-spin text-primary" />
//         <p className="text-sm text-muted-foreground animate-pulse">
//           Loading...
//         </p>
//       </div>
//     </div>
//   );
// };

const ErrorBoundary = () => {
  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-white pt-[80px] pb-8 px-4">
      <SelfServiceNav />
      <Card className="w-full max-w-4xl mx-auto shadow pt-6">
        <CardContent>
          <div className="flex flex-col items-center justify-center text-gray-400 p-4 gap-4">
            <SearchX size={200} />
            <p>No invoice found.</p>
            <Button size="sm" onClick={() => navigate("/self-service")}>
              Back
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SelfServiceOcrDocumentPage;
